import { Component, OnInit, inject, PLATFORM_ID, Renderer2 } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { loadCompanies } from './core/state/company/company-context.actions';
import { Store } from '@ngrx/store';
import { FirstConfigurationComponent } from './features/first-configuration/components/first-configuration/first-configuration.component';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BehaviorSubject, Subject } from 'rxjs';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import Keycloak, { KeycloakProfile } from 'keycloak-js';
import { EKOWITRYNA_UI_VERSION } from '@ekowitryna/ui';
import { environment } from '../environments/environment';
import {
  clearStoredReturnUrl,
  getStoredReturnUrl,
  isAllowedReturnUrl,
  storeReturnUrlFromQuery,
} from './shared/helpers/return-url.utils';
import { getRealmRoles, needsKeycloakLogin } from './shared/helpers/keycloak-auth.helper';

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  providers: [DialogService],
})
export class AppComponent implements OnInit {

  firstConfigurationDialog: DynamicDialogRef | null = null;
  isHandset = false;
  private profile: Subject<any> = new BehaviorSubject<any>(null);
  public profile$ = this.profile.asObservable();
  hasAccess = true; // Nowa właściwość dla kontroli dostępu

  private breakpointObserver = inject(BreakpointObserver);
  private store = inject(Store);
  private dialogService = inject(DialogService);
  private keycloak = inject(Keycloak);
  private renderer = inject(Renderer2);
  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);

  constructor() {
    this.addLibraryVersionComment();
  }

  private addLibraryVersionComment(): void {
    if (isPlatformBrowser(this.platformId)) {
      const comment = this.renderer.createComment(` @ekowitryna/ui: ${EKOWITRYNA_UI_VERSION} `);
      const head = this.document.head;
      this.renderer.appendChild(head, comment);
    }
  }


  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      storeReturnUrlFromQuery(environment.allowedReturnUrlOrigins);
    }

    void this.checkUserAccess();

    this.breakpointObserver.observe(Breakpoints.Handset)
      .subscribe(result => {
        this.isHandset = result.matches;
      });
  }

  private async checkUserAccess(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    await this.waitForKeycloakInitialization();

    if (await needsKeycloakLogin(this.keycloak)) {
      await this.keycloak.login({
        redirectUri: window.location.href
      });
      return;
    }

    const userRoles = getRealmRoles(this.keycloak);
    this.hasAccess = userRoles.includes(KeycloakRole.COMPANY);

    if (this.hasAccess) {
      this.store.dispatch(loadCompanies());
      this.checkFirstConfiguration();
    }
  }

  private async waitForKeycloakInitialization(): Promise<void> {
    const timeoutMs = 10_000;
    const pollMs = 50;
    const startedAt = Date.now();

    while (!this.keycloak.didInitialize && Date.now() - startedAt < timeoutMs) {
      await new Promise(resolve => setTimeout(resolve, pollMs));
    }
  }

  checkFirstConfiguration() {
    if (!this.keycloak.authenticated || !this.hasAccess) {
      return;
    }

    this.keycloak.loadUserProfile().then((profile: KeycloakProfile) => {
      const attributes = (profile as KeycloakProfile & { attributes?: any }).attributes;
      // Tylko użytkownicy z rolą COMPANY mogą przejść przez pierwszą konfigurację
      const realmAccess = this.keycloak.realmAccess;
      const userRoles = realmAccess?.roles || [];

      if (attributes?.marketingAgreement == null && userRoles.includes(KeycloakRole.COMPANY)) {
        this.showFirstConfiguration();
      }
    });
  }

  getAccountType(): AccountType {
    const realmAccess = this.keycloak.realmAccess;
    const userRoles = realmAccess?.roles || [];
    return userRoles.includes(KeycloakRole.COMPANY) ? AccountType.COMPANY : AccountType.EMPLOYEE;
  }

  showFirstConfiguration() {
    const returnUrl = getStoredReturnUrl() ?? undefined;

    this.firstConfigurationDialog = this.dialogService.open(FirstConfigurationComponent, {
      closable: false,
      header: 'Witamy w ekowitrynie',
      width: this.isHandset ? '100%' : '65%',
      modal: true,
      dismissableMask: false,
      data: { returnUrl },
    });

    this.firstConfigurationDialog!.onClose.subscribe((returnUrlFromDialog?: string) => {
      const url = returnUrlFromDialog ?? getStoredReturnUrl();
      if (url && isAllowedReturnUrl(url, environment.allowedReturnUrlOrigins)) {
        clearStoredReturnUrl();
        window.location.href = url;
        return;
      }

      clearStoredReturnUrl();
      window.location.reload();
    });
  }


}

export enum AccountType {
  EMPLOYEE = "EMPLOYEE",
  COMPANY = "COMPANY"
}

export enum KeycloakRole {
  COMPANY = 'COMPANY',
  EMPLOYEE = 'EMPLOYEE',
  REPORTS_ARCHIVAL_YEARS = 'REPORTS_ARCHIVAL_YEARS',
  REPORTS_ACTUAL_YEAR = 'REPORTS_ACTUAL_YEAR',
  WASTE_CARDS_ACCESS = 'WASTE_CARDS_ACCESS',
}
